/**
 * useDeviceType Hook
 *
 * Detects the current device type based on viewport width and touch capability.
 * Returns 'desktop', 'tablet', or 'mobile'.
 */

import { useState, useEffect, useCallback } from 'react';

export type DeviceType = 'desktop' | 'tablet' | 'mobile';

interface DeviceBreakpoints {
  mobile: number;
  tablet: number;
}

const DEFAULT_BREAKPOINTS: DeviceBreakpoints = {
  mobile: 768,
  tablet: 1024,
};

export interface UseDeviceTypeOptions {
  /** Custom breakpoints for device detection */
  breakpoints?: Partial<DeviceBreakpoints>;
  /** Whether to consider touch capability */
  considerTouch?: boolean;
}

export interface UseDeviceTypeResult {
  /** Current device type */
  deviceType: DeviceType;
  /** Whether the device is mobile */
  isMobile: boolean;
  /** Whether the device is tablet */
  isTablet: boolean;
  /** Whether the device is desktop */
  isDesktop: boolean;
  /** Whether the device has touch capability */
  hasTouch: boolean;
  /** Current viewport width */
  viewportWidth: number;
  /** Whether the device is in portrait orientation */
  isPortrait: boolean;
  /** Whether the device is in landscape orientation */
  isLandscape: boolean;
}

/**
 * Check if the device has touch capability
 */
function detectTouchCapability(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Determine device type based on viewport width
 */
function getDeviceType(
  width: number,
  breakpoints: DeviceBreakpoints
): DeviceType {
  if (width < breakpoints.mobile) {
    return 'mobile';
  }
  if (width < breakpoints.tablet) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * Hook for detecting device type
 */
export function useDeviceType(
  options: UseDeviceTypeOptions = {}
): UseDeviceTypeResult {
  const { breakpoints: customBreakpoints = {}, considerTouch = true } = options;

  const breakpoints: DeviceBreakpoints = {
    ...DEFAULT_BREAKPOINTS,
    ...customBreakpoints,
  };

  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 768
  );

  const [hasTouch, setHasTouch] = useState(() => detectTouchCapability());

  const handleResize = useCallback(() => {
    setViewportWidth(window.innerWidth);
    setViewportHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    // Set initial values
    setViewportWidth(window.innerWidth);
    setViewportHeight(window.innerHeight);
    setHasTouch(detectTouchCapability());

    // Add resize listener
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // Calculate device type
  let deviceType = getDeviceType(viewportWidth, breakpoints);

  // Optionally adjust based on touch capability
  // A touch-capable device with a large screen might still be a tablet
  if (considerTouch && hasTouch && deviceType === 'desktop') {
    // Large touch device is likely a tablet
    deviceType = 'tablet';
  }

  // Calculate orientation
  const isPortrait = viewportHeight > viewportWidth;
  const isLandscape = viewportWidth >= viewportHeight;

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    hasTouch,
    viewportWidth,
    isPortrait,
    isLandscape,
  };
}

export default useDeviceType;
